import { useMemo } from 'react';
import { useGetV4ManufacturerByRouteManufacturerIdSearchQuery } from 'randmar-api-client';

/**
 * Custom hook for handling the manufacturer search logic.
 * It fetches, transforms, and returns the search results.
 * @param query The search string.
 * @param applicationId The current manufacturer's application ID.
 * @returns The search results and loading state.
 */

const toCamelCase = (() => {
  const camelCaseCache = new Map<string, string>();
  return (str: string): string => {
    if (camelCaseCache.has(str)) {
      return camelCaseCache.get(str)!;
    }
    const result = str.length > 0 ? str.charAt(0).toLowerCase() + str.slice(1) : str;
    camelCaseCache.set(str, result);
    return result;
  };
})();

const convertObjectKeysToCamelCase = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(item => convertObjectKeysToCamelCase(item));
  }

  if (data !== null && typeof data === 'object') {
    // Avoid converting instances of classes like Date, which would otherwise become empty objects.
    if (Object.prototype.toString.call(data) !== '[object Object]') {
      return data;
    }

    return Object.entries(data).reduce((acc: { [key: string]: any }, [key, value]) => {
      const newKey = toCamelCase(key);
      acc[newKey] = convertObjectKeysToCamelCase(value);
      return acc;
    }, {});
  }

  return data;
};

export const useManufacturerSearch = (query: string, applicationId: string) => {
  const { data: searchResults, isLoading, isFetching } = useGetV4ManufacturerByRouteManufacturerIdSearchQuery(
    {
      routeManufacturerId: applicationId,
      s: query,
    },
    {
      skip: !query, // Don't run the query if it's empty
    }
  );

  const convertedResults = useMemo(() => {
    if (!searchResults || !Array.isArray(searchResults)) return [];

    return searchResults.filter(r => r?.Content).map(r => ({
      contentType: r.ContentType,
      priority: r.Content.Priority || 'Normal',
      content: convertObjectKeysToCamelCase(r.Content),
      newContent: r.Content,
    }));
  }, [searchResults]);

  return {
    results: convertedResults,
    isLoading: isLoading || isFetching,
  };
};
